'use client';
import { useTranslation } from '@root/src/i18n/client';
import Image from '@components/common/ImageFallback';

interface Content {
  id: string | number;
  title: string;
  slug: string;
  description?: string;
  image?: string[] | string;
  createdAt?: string;
  author?: string;
  content?: string;
}

interface Topic {
  id: string | number;
  name: string;
  slug: string;
}

interface ContentDetailProps {
  topic: Topic;
  content: Content;
  relatedContents: Content[];
  onSelectContent: (content: Content) => void;
  onBack: () => void;
}

export default function ContentDetail({
  topic,
  content,
  relatedContents,
  onSelectContent,
  onBack,
}: ContentDetailProps) {
  const { t } = useTranslation('common');

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    });
  };

  const getImageUrl = (image: string[] | string | undefined) => {
    if (!image) return null;
    if (Array.isArray(image)) return image[0];
    return image;
  };

  const mainImageUrl = getImageUrl(content.image);

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Breadcrumb & Back */}
        <div className="mb-6 pb-6 border-b border-[#EDE6D8]">
          <button
            onClick={onBack}
            className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-[#7F6B48] hover:text-[#223A27] transition-colors"
          >
            ← {t('common.back', 'Quay lại')}
          </button>
          <div className="text-xs uppercase tracking-[0.1em] text-[#7F6B48] font-semibold mb-2">
            {topic.name}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#223A27]">
            {content.title}
          </h1>
        </div>

        {/* Meta Information */}
        <div className="mb-6 flex flex-wrap gap-4 text-sm">
          {content.createdAt && (
            <div className="flex items-center gap-2 text-[#7F6B48]">
              <span className="text-lg">📅</span>
              <span>{formatDate(content.createdAt)}</span>
            </div>
          )}
          {content.author && (
            <div className="flex items-center gap-2 text-[#7F6B48]">
              <span className="text-lg">✍️</span>
              <span>{content.author}</span>
            </div>
          )}
        </div>

        {/* Featured Image */}
        {mainImageUrl && (
          <div className="relative w-full h-80 rounded-[24px] overflow-hidden mb-8 bg-gradient-to-br from-[#E8DCC8] to-[#D4C4A8]">
            <Image
              src={mainImageUrl}
              alt={content.title}
              fill
              className="object-cover"
            />
          </div>
        )}

        {/* Content Body */}
        {content.description && (
          <div className="prose max-w-none mb-8">
            <p className="text-base text-[#5C5A52] leading-8 whitespace-pre-wrap">
              {content.description}
            </p>
          </div>
        )}

        {content.content && (
          <div className="prose max-w-none">
            <div
              className="text-base text-[#5C5A52] leading-8"
              dangerouslySetInnerHTML={{
                __html: content.content,
              }}
            />
          </div>
        )}
      </div>

      {/* Sidebar - Related Content */}
      <aside className="w-full lg:w-80">
        <div className="sticky top-20 rounded-[24px] border border-[#EDE6D8] bg-[#FCF7EE] p-6">
          <h3 className="text-lg font-bold text-[#223A27] mb-6">
            {t('common.relatedArticles', 'Bài viết liên quan')}
          </h3>

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {relatedContents && relatedContents.length > 0 ? (
              relatedContents.map((relatedContent) => {
                const isActive = relatedContent.slug === content.slug;
                const sidebarImageUrl = getImageUrl(relatedContent.image);

                return (
                  <div
                    key={relatedContent.id}
                    className={`group p-3 rounded-lg cursor-pointer transition-all duration-300 ${
                      isActive
                        ? 'bg-[#E8DCC8] border border-[#D4C4A8]'
                        : 'hover:bg-white border border-transparent hover:border-[#EDE6D8]'
                    }`}
                    onClick={() => !isActive && onSelectContent(relatedContent)}
                  >
                    {/* Thumbnail */}
                    <div className="relative w-full h-20 rounded-md overflow-hidden mb-2 bg-gradient-to-br from-[#E8DCC8] to-[#D4C4A8]">
                      {sidebarImageUrl ? (
                        <Image
                          src={sidebarImageUrl}
                          alt={relatedContent.title}
                          fill
                          className="object-cover"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-xl text-[#223A27]/20">
                          📄
                        </div>
                      )}
                    </div>

                    {/* Title */}
                    <h4
                      className={`text-sm font-semibold line-clamp-2 transition-colors ${
                        isActive
                          ? 'text-[#223A27]'
                          : 'text-[#223A27] group-hover:text-[#7F6B48]'
                      }`}
                    >
                      {relatedContent.title}
                    </h4>

                    {/* Date */}
                    {relatedContent.createdAt && (
                      <div className="text-xs text-[#7F6B48] mt-1">
                        {formatDate(relatedContent.createdAt)}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-[#666]">
                {t('common.noRelatedArticles', 'Không có bài viết liên quan')}
              </p>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
